import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';

const Waitlist = () => {
  // Data state
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Selection state
  const [selected, setSelected] = useState([]);
  
  // Filter & pagination
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  
  // Dialog state
  const [notifyDialog, setNotifyDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [notifying, setNotifying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, entriesRes] = await Promise.all([
        adminAPI.getWaitlistStats(),
        adminAPI.getWaitlist({ page: page + 1, limit: rowsPerPage, filter }),
      ]);
      
      setStats(statsRes.data);
      setEntries(entriesRes.data.entries);
      setTotal(entriesRes.data.total);
    } catch (err) {
      setError('Failed to load waitlist data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Selection handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(entries.map(e => e.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    setSelected(prev => 
      prev.includes(id) 
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  const isSelected = (id) => selected.includes(id);

  // Notify handlers
  const handleNotifyOpen = () => {
    if (selected.length === 0) {
      setSnackbar({ open: true, message: 'Select entries to notify', severity: 'warning' });
      return;
    }
    setNotifyDialog(true);
  };

  const handleNotify = async (sendTest = false) => {
    setNotifying(true);
    try {
      if (sendTest && testEmail) {
        await adminAPI.notifyWaitlist({ testEmail });
        setSnackbar({ open: true, message: `Test email sent to ${testEmail}`, severity: 'success' });
      } else {
        // Notify selected users - we need to notify them by sending their count
        // Since the API notifies in order, we'll notify the count equal to selected
        const result = await adminAPI.notifyWaitlist({ limit: selected.length });
        setSnackbar({ 
          open: true, 
          message: result.data.message || `Notified ${result.data.sent} people`, 
          severity: 'success' 
        });
        setSelected([]);
        fetchData();
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to send notifications', severity: 'error' });
    } finally {
      setNotifying(false);
      setNotifyDialog(false);
      setTestEmail('');
    }
  };

  // Delete handlers
  const handleDeleteOpen = () => {
    if (selected.length === 0) {
      setSnackbar({ open: true, message: 'Select entries to delete', severity: 'warning' });
      return;
    }
    setDeleteDialog(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      let deleted = 0;
      for (const id of selected) {
        try {
          await adminAPI.deleteWaitlistEntry(id);
          deleted++;
        } catch (err) {
          console.error('Failed to delete:', id);
        }
      }
      setSnackbar({ open: true, message: `Deleted ${deleted} entries`, severity: 'success' });
      setSelected([]);
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete entries', severity: 'error' });
    } finally {
      setDeleting(false);
      setDeleteDialog(false);
    }
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Get status chip
  const getStatusChip = (entry) => {
    if (entry.converted_at) {
      return <Chip size="small" label="Converted" color="success" icon={<CheckCircleIcon />} />;
    }
    if (entry.notified_at) {
      return <Chip size="small" label="Notified" color="info" icon={<EmailIcon />} />;
    }
    return <Chip size="small" label="Waiting" color="default" icon={<ScheduleIcon />} />;
  };

  // Stat card component
  const StatCard = ({ title, value, icon, color = 'primary' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={600}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: `${color}.lighter`,
            color: `${color}.main`,
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" fontWeight={600}>
          Waitlist
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              title="Total on Waitlist"
              value={stats.total}
              icon={<PeopleIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              title="Waiting to Notify"
              value={stats.notNotified}
              icon={<ScheduleIcon />}
              color="warning"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              title="Notified"
              value={stats.notified}
              icon={<EmailIcon />}
              color="info"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              title="Converted"
              value={stats.converted}
              icon={<PersonAddIcon />}
              color="success"
            />
          </Grid>
        </Grid>
      )}

      {/* Actions Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            label="Filter"
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="not_notified">Waiting</MenuItem>
            <MenuItem value="notified">Notified</MenuItem>
            <MenuItem value="converted">Converted</MenuItem>
          </TextField>

          <Box sx={{ flexGrow: 1 }} />

          {selected.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {selected.length} selected
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            startIcon={<EmailIcon />}
            onClick={handleNotifyOpen}
            disabled={selected.length === 0}
          >
            Notify Selected ({selected.length})
          </Button>

          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteOpen}
            disabled={selected.length === 0}
          >
            Delete
          </Button>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < entries.length}
                  checked={entries.length > 0 && selected.length === entries.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Email</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Phone</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell>Status</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Notified</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Converted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {filter === 'all' ? 'No one on the waitlist yet' : `No ${filter.replace('_', ' ')} entries`}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => (
                <TableRow 
                  key={entry.id}
                  hover
                  selected={isSelected(entry.id)}
                  onClick={() => handleSelect(entry.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={isSelected(entry.id)} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {entry.email}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body2" color="text.secondary">
                      {entry.phone || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(entry.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(entry)}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(entry.notified_at)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(entry.converted_at)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* Notify Dialog */}
      <Dialog open={notifyDialog} onClose={() => setNotifyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send "Spots Available" Notification</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will send an email to <strong>{selected.length}</strong> selected waitlist entries 
            letting them know spots have opened up.
          </DialogContentText>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            Entries are notified in the order they joined (oldest first). 
            Their status will be updated to "Notified".
          </Alert>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Test First (optional)
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="your@email.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            helperText="Send a test email to yourself first"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyDialog(false)} disabled={notifying}>
            Cancel
          </Button>
          {testEmail && (
            <Button 
              onClick={() => handleNotify(true)} 
              disabled={notifying}
              color="secondary"
            >
              {notifying ? <CircularProgress size={20} /> : 'Send Test'}
            </Button>
          )}
          <Button 
            onClick={() => handleNotify(false)} 
            variant="contained" 
            disabled={notifying}
          >
            {notifying ? <CircularProgress size={20} /> : `Notify ${selected.length} People`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Waitlist Entries</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{selected.length}</strong> entries from the waitlist?
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Waitlist;
